import { createContext, useContext } from 'react'
import type { AssistantOpenRequest, AssistantState } from '@/features/ai-chat/assistantSessions'
import type { DockMode } from '@/features/ai-chat/dockModeStorage'
import type { ChatMessage, ChatTurn } from '@/features/ai-chat/types'

/** 会话容器（有标签栏时是 tabpanel）的 DOM id */
export const tabPanelId = (sessionId: string) => `assistant-panel-${sessionId}`

/** 一个标签（会话）对窗口外壳暴露的状态：小条 / 顶栏圆点 / 关闭确认都看它。 */
export interface ConversationStatus {
  streaming: boolean
  awaitingConfirmation: boolean
  /** 不在前台（窗口最小化或不是当前标签）期间来了新内容 */
  unread: boolean
  /** 未读的是一条错误（§6 第 10 点：单独显示「Reply failed」） */
  failed: boolean
  /** New chat 可用：有内容可清 */
  canReset: boolean
}

/** 会话宿主上报给 Provider 的可持久化部分（F5 恢复） */
export interface ConversationSnapshot { conversationId: string; messages: ChatMessage[]; streaming: boolean; turn: ChatTurn | null }

/** 小条 / 顶栏上显示的汇总状态，按优先级取一个 */
export type AssistantAttention = 'awaitingConfirmation' | 'replying' | 'replyFailed' | 'newReply' | null

export interface AssistantWindowApi {
  state: AssistantState
  /** 多入口共用（§1 第 2 点）：同一入口 + 对象切到已有标签，否则新开；满 5 个返回 `limit` 并提示 */
  openAssistant: (request: AssistantOpenRequest) => 'opened' | 'activated' | 'limit'
  minimize: () => void
  restore: () => void
  /** 直接关（不问）。要问的场景由窗口的关闭按钮先判断 `needsCloseConfirm` */
  closeWindow: () => void
  attention: AssistantAttention
  /** 关窗前要确认：任一会话在回复或有卡待确认 */
  needsCloseConfirm: boolean
  /** 切到某个标签（会话） */
  activate: (sessionId: string) => void
  /** 关掉一个标签 = 结束这条会话（不问；要问的场景由标签的关闭按钮先判断）。关掉最后一个 = 关窗 */
  closeSession: (sessionId: string) => void
}

export interface AssistantWindowInternalApi extends AssistantWindowApi {
  /** 用户选的模式；窄屏不停靠时 `docked` 为 false，但这个值不变 */
  dockMode: DockMode
  setDockMode: (mode: DockMode) => void
  /** 视口够不够宽停靠（窄屏让不出面板宽度）；false 时切换按钮置灰并说明原因 */
  canDock: boolean
  /** 此刻窗口是不是停靠着（选了停靠 + 窗口打开 + 视口够宽） */
  docked: boolean
  /** 页面主体要让出的右侧宽度（px）；不停靠 = 0 */
  dockInset: number
  statuses: Record<string, ConversationStatus>
  onStatus: (sessionId: string, status: ConversationStatus) => void
  onRegisterReset: (sessionId: string, reset: (() => void) | null) => void
  resetSession: (sessionId: string) => void
  /** 刷新恢复：这个会话起步用的 conversationId 与消息（没有 = 新会话） */
  getRestored: (sessionId: string) => { conversationId: string; messages: ChatMessage[] } | undefined
  onPersist: (sessionId: string, snapshot: ConversationSnapshot) => void
  /** 顶栏按钮登记自己：最小化 / 关闭后焦点回到它 */
  triggerRef: React.MutableRefObject<HTMLElement | null>
}

export const AssistantWindowContext = createContext<AssistantWindowInternalApi | null>(null)

export function useAssistantWindowInternal(): AssistantWindowInternalApi {
  const ctx = useContext(AssistantWindowContext)
  // 没有 provider = 挂载位置错了（应在 Layout 里包住顶栏与页面），直接报错，不静默降级
  if (!ctx) throw new Error('useAssistantWindow must be used inside <AssistantWindowProvider>')
  return ctx
}

export function useAssistantWindow(): AssistantWindowApi { return useAssistantWindowInternal() }
